import * as React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { get, uniqueId, isEmpty } from 'lodash';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useContentContext } from '@qbe/dfc-content';
import { FormGroupLabel, Button, Icon, IconButton } from '@qbe/ads-core';
import { DetailedList } from '@qbe/ads-blocks-forms';

import { FormSpacer, InformationPopUp } from 'components';
import { DependentData, useFormState, FormDataType } from 'contexts/FormProvider';

import { dependentNameSchema, dependentAgeSchema } from '../schema';
import DependentItem from './DependentItem';
import { useContentComplaintPartners } from 'utils/content';
import { List } from '@qbe/ads-blocks-forms/src/DetailedList/DetailedList.type';

const StyledDetailedList = styled(DetailedList)`
  padding-bottom: 0;

  .ListGroup__list-action {
    margin-top: 1rem;
  }
  .ListGroup__custom-icon {
    display: block;
  }
`;

interface DependentProps {
  form: UseFormReturn<FormDataType>;
  errorMessage: string | null;
}

const MAX_DEPENDENTS = 10;

const errorMessageName = 'Please enter a valid name without special characters';
const errorMessageAge = 'Please enter a valid numeric value';

const Dependents = ({ form, errorMessage }: DependentProps) => {
  const {
    register,
    watch,
    setValue,
    setError,
    clearErrors: clearError,
    formState: { errors },
    trigger: triggerValidation,
    control,
  } = form;

  const { getTextContent } = useContentComplaintPartners({
    context: useContentContext(),
  });

  const { formData } = useFormState();

  const dependents = watch('dependents', formData?.dependents);
  const newDependentList = dependents.filter((dependent) => !isEmpty(dependent.dependentName));

  const [dependentList, setDependentList] = React.useState<DependentData[]>(newDependentList);

  React.useEffect(() => {
    setValue('dependents', dependentList);
  }, [dependentList, setValue]);

  const handleClose = (index: number) => () => {
    setDependentList(dependentList.filter((_, i) => i !== index));
    clearError(`dependents[${index}]` as any);
  };

  const handleInputChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const values = dependentList;
    const fieldName = `dependents[${index}].dependentName`;
    const fieldAge = `dependents[${index}].dependentAge`;

    if (e.target.name.endsWith('dependentName')) {
      values[index].dependentName = e.target.value;
      const isValidName = dependentNameSchema.isValidSync(e.target.value);

      if (
        (!isEmpty(values[index].dependentName) && !isValidName) ||
        (isEmpty(values[index].dependentName) && !isEmpty(values[index].dependentAge))
      ) {
        setError(fieldName as any, { type: 'invalid-name', message: errorMessageName });
      }

      if (isEmpty(values[index].dependentAge)) {
        if (!isEmpty(values[index].dependentName)) {
          setError(fieldAge as any, { type: 'invalid-age', message: errorMessageAge });
        }
      }
      const isValidAge = dependentAgeSchema.isValidSync(values[index].dependentAge);

      if (isValidAge) {
        clearError(fieldAge as any);
      }

      if (isValidName) {
        clearError(fieldName as any);
      }
    } else {
      values[index].dependentAge = e.target.value;
      const isValidAge = dependentAgeSchema.isValidSync(values[index].dependentAge);

      if (
        (!isEmpty(values[index].dependentAge) && !isValidAge) ||
        (isEmpty(values[index].dependentAge) && !isEmpty(values[index].dependentName))
      ) {
        setError(fieldAge as any, { type: 'invalid-age', message: errorMessageAge });
      }
      if (isEmpty(values[index].dependentName)) {
        if (!isEmpty(values[index].dependentAge)) {
          setError(fieldName as any, { type: 'invalid-name', message: errorMessageName });
        }
      }
      const isValidName = dependentNameSchema.isValidSync(values[index].dependentName);

      if (isValidName) {
        clearError(fieldName as any);
      }

      if (isValidAge) {
        clearError(fieldAge as any);
      }
    }

    setDependentList(values);
  };

  const handleAddDependents = () =>
    setDependentList([...dependentList, { id: uniqueId(), dependentName: '', dependentAge: '' }]);

  const isAllowedToAddDependents = dependentList.length < MAX_DEPENDENTS;

  return (
    <>
      <FormGroupLabel
        alignCenter
        title={getTextContent('fha.insured.customer.dependents.heading')}
        titleSuffix={<InformationPopUp message={getTextContent('fha.insured.customer.dependents.infoText')} />}
        subtitle={getTextContent('fha.insured.customer.dependents')}
      />
      <>
        <FormSpacer type="input" />
        <>
          <Button
            onClick={handleAddDependents}
            variant="secondary-on-light"
            label={getTextContent('fha.insured.customer.dependents.btn')}
            endIcon={<Icon muiIcon={AddIcon} />}
            disabled={!isAllowedToAddDependents}
          ></Button>
        </>
      </>

      {dependentList.length > 0 && (
        <StyledDetailedList
          variant="stack"
          list={
            [
              ...dependentList.map((dependent, index) => ({
                label: '',
                id: `dependent-${index}`,
                name: index,
                stackActions: () => (
                  <IconButton
                    onClick={handleClose(index)}
                    variant="dark"
                    iconProps={{
                      muiIcon: CloseIcon,
                    }}
                  />
                ),
                value: (
                  <>
                    <>
                      <DependentItem
                        fieldNames={{
                          name: `dependents[${index}].dependentName`,
                          age: `dependents[${index}].dependentAge`,
                        }}
                        defaultValues={{
                          name: get(dependent, 'dependentName', index),
                          age: get(dependent, 'dependentAge', index),
                        }}
                        changeEvents={{
                          onChangeName: handleInputChange(index),
                          onChangeAge: handleInputChange(index),
                        }}
                        errors={{
                          name: get(errors, `dependents[${index}].dependentName.message`, '') as string,
                          age: get(errors, `dependents[${index}].dependentAge.message`, '') as string,
                        }}
                      />
                    </>
                  </>
                ),
              })),
            ] as List[]
          }
        />
      )}

      <>{errorMessage && <p>{errorMessage}</p>}</>
    </>
  );
};

export default Dependents;
